import { isKnownDirective, isKnownTextDirective, resolveDirective } from './directives.js';
// ---------------------------------------------------------------- mdast -> PM
export function mdastToDoc(body) {
    const content = blocksToPM(body.children);
    // ProseMirror's `doc` requires `block+`. An empty file still needs a node.
    return { type: 'doc', content: content.length > 0 ? content : [{ type: 'paragraph' }] };
}
function blocksToPM(nodes) {
    return nodes.flatMap(blockToPM);
}
function blockToPM(node) {
    switch (node.type) {
        case 'paragraph':
            return [{ type: 'paragraph', content: inlinesToPM(node.children) }];
        case 'heading':
            return [
                {
                    type: 'heading',
                    attrs: { level: node.depth },
                    content: inlinesToPM(node.children),
                },
            ];
        case 'code':
            return [
                {
                    type: 'codeBlock',
                    // `meta` as well as `lang`: it carries the filename, the highlighted
                    // lines and every other renderer option, and dropping it deleted them
                    // from the file on the first save.
                    attrs: { language: node.lang ?? null, meta: node.meta ?? null },
                    content: node.value === '' ? [] : [{ type: 'text', text: node.value }],
                },
            ];
        case 'blockquote':
            return [{ type: 'blockquote', content: nonEmptyBlocks(node.children) }];
        case 'list': {
            const type = node.ordered === true ? 'orderedList' : 'bulletList';
            const attrs = { spread: node.spread ?? false };
            if (node.ordered === true)
                attrs['start'] = node.start ?? 1;
            return [
                {
                    type,
                    attrs,
                    content: node.children.map((item) => ({
                        type: 'listItem',
                        attrs: { checked: item.checked ?? null },
                        content: nonEmptyBlocks(item.children),
                    })),
                },
            ];
        }
        case 'thematicBreak':
            return [{ type: 'horizontalRule' }];
        case 'table':
            return [tableToPM(node)];
        case 'containerDirective':
        case 'leafDirective':
            return [directiveToPM(node)];
        default:
            return [{ type: 'unknownBlock', attrs: { mdast: structuredClone(node) } }];
    }
}
function nonEmptyBlocks(children) {
    const content = blocksToPM(children);
    return content.length > 0 ? content : [{ type: 'paragraph' }];
}
function tableToPM(node) {
    const align = (node.align ?? []).map((a) => a ?? null);
    return {
        type: 'table',
        attrs: { align },
        content: node.children.map((row, rowIndex) => ({
            type: 'tableRow',
            content: row.children.map((cell) => ({
                type: 'tableCell',
                attrs: { header: rowIndex === 0 },
                content: inlinesToPM(cell.children),
            })),
        })),
    };
}
function directiveToPM(node) {
    const kind = node.type === 'containerDirective' ? 'container' : 'leaf';
    /**
     * The name this directive means, and the attributes its spelling implies.
     *
     * `:::warning` is a callout in Docusaurus, VitePress and Mintlify, so
     * somebody who types it here means a callout — reading it as an opaque block
     * is lossless on paper and a bug in practice: the editor shows something it
     * cannot edit and the site renders nothing. See `DIRECTIVE_ALIASES`, and
     * `VOCABULARY_VERSION` for why the list has a version at all.
     */
    const resolved = resolveDirective(node.name, node.attributes ?? {});
    const attributes = (resolved?.attributes ?? node.attributes ?? {});
    if (resolved) {
        switch (resolved.name) {
            case 'hint':
                // Preserve the authored value, including its absence. Normalising here
                // would rewrite `:::hint` to `:::hint{type="note"}` on every save.
                // `normaliseHintType` belongs to rendering, not to the model.
                return {
                    type: 'hint',
                    attrs: { type: attributes['type'] ?? null, title: attributes['title'] ?? null },
                    content: nonEmptyBlocks(node.children),
                };
            case 'snippet':
                // A leaf with no children of its own: the reference IS the content.
                return { type: 'snippet', attrs: { name: attributes['name'] ?? '' } };
            case 'field':
                return {
                    type: 'field',
                    attrs: {
                        name: attributes['name'] ?? null,
                        type: attributes['type'] ?? null,
                        // Directive attributes are strings; `required` is a flag, so its
                        // presence is the truth and any value but "false" means yes.
                        required: attributes['required'] !== undefined && attributes['required'] !== 'false',
                        default: attributes['default'] ?? null,
                        in: attributes['in'] ?? null,
                    },
                    content: nonEmptyBlocks(node.children),
                };
            case 'tabs':
                return {
                    type: 'tabs',
                    attrs: { group: attributes['group'] ?? null },
                    content: blocksToPM(node.children),
                };
            case 'tab':
                return {
                    type: 'tab',
                    attrs: {
                        title: attributes['title'] ?? null,
                        value: attributes['value'] ?? null,
                        // A flag: written as a bare `default`, so its presence is the
                        // truth and any value but "false" means yes.
                        default: attributes['default'] !== undefined && attributes['default'] !== 'false',
                    },
                    content: nonEmptyBlocks(node.children),
                };
            case 'expandable':
                return {
                    type: 'expandable',
                    attrs: { title: attributes['title'] ?? null },
                    content: nonEmptyBlocks(node.children),
                };
            case 'steps':
                return { type: 'steps', content: blocksToPM(node.children) };
            case 'step':
                return {
                    type: 'step',
                    attrs: { title: attributes['title'] ?? null },
                    content: nonEmptyBlocks(node.children),
                };
            case 'figure':
                return {
                    type: 'figure',
                    attrs: {
                        src: attributes['src'] ?? null,
                        alt: attributes['alt'] ?? null,
                        width: attributes['width'] ?? null,
                        srcDark: attributes['srcDark'] ?? null,
                    },
                    content: blocksToPM(node.children),
                };
            case 'embed':
                return {
                    type: 'embed',
                    attrs: { url: attributes['url'] ?? null },
                    content: blocksToPM(node.children),
                };
            case 'cards':
                return { type: 'cards', content: blocksToPM(node.children) };
            case 'card':
                return {
                    type: 'card',
                    attrs: {
                        title: attributes['title'] ?? null,
                        href: attributes['href'] ?? null,
                        icon: attributes['icon'] ?? null,
                    },
                    content: blocksToPM(node.children),
                };
            case 'file':
                return {
                    type: 'file',
                    attrs: { src: attributes['src'] ?? null, name: attributes['name'] ?? null },
                    content: blocksToPM(node.children),
                };
            case 'button':
                // Written as a leaf directive: the label is one line of phrasing
                // content, not a block, so it needs no wrapping paragraph the way a
                // container's content does.
                return {
                    type: 'button',
                    attrs: { href: attributes['href'] ?? null, style: attributes['style'] ?? null },
                    content: inlinesToPM((node.children ?? [])),
                };
            case 'codegroup':
                // Children are `code` nodes directly — no per-tab wrapper the way
                // `tabs`/`tab` needs one, because a fenced code block already carries
                // its own label (the language, or `title=` in its meta string).
                return { type: 'codeGroup', content: blocksToPM(node.children) };
        }
    }
    return {
        type: 'genericDirective',
        attrs: { name: node.name, attributes, kind },
        content: blocksToPM(node.children),
    };
}
function inlinesToPM(nodes, marks = []) {
    return nodes.flatMap((node) => inlineToPM(node, marks));
}
/**
 * A mark type may appear at most once on a text node — ProseMirror rejects
 * `em,em` outright. Markdown happily nests `*a *b* c*`, so the outer mark wins
 * and the inner one is absorbed.
 */
function addMark(marks, mark) {
    return marks.some((m) => m.type === mark.type) ? marks : [...marks, mark];
}
function inlineToPM(node, marks) {
    const withMark = (mark, children) => inlinesToPM(children, addMark(marks, mark));
    switch (node.type) {
        case 'text':
            return node.value === '' ? [] : [textNode(node.value, marks)];
        case 'strong':
            return withMark({ type: 'strong' }, node.children);
        case 'emphasis':
            return withMark({ type: 'em' }, node.children);
        case 'delete':
            return withMark({ type: 'strike' }, node.children);
        case 'link':
            return withMark({ type: 'link', attrs: { href: node.url, title: node.title ?? null } }, node.children);
        case 'inlineCode':
            return [textNode(node.value, addMark(marks, { type: 'code' }))];
        case 'textDirective':
            if (isKnownTextDirective(node.name)) {
                return withMark({ type: node.name }, node.children);
            }
            return [
                {
                    type: 'unknownInline',
                    attrs: { mdast: structuredClone(node) },
                    ...(marks.length > 0 ? { marks } : {}),
                },
            ];
        case 'image':
            return [
                {
                    type: 'image',
                    attrs: { src: node.url, alt: node.alt ?? null, title: node.title ?? null },
                    ...(marks.length > 0 ? { marks } : {}),
                },
            ];
        case 'break':
            return [{ type: 'hardBreak', ...(marks.length > 0 ? { marks } : {}) }];
        default:
            return [
                {
                    type: 'unknownInline',
                    attrs: { mdast: structuredClone(node) },
                    ...(marks.length > 0 ? { marks } : {}),
                },
            ];
    }
}
function textNode(text, marks) {
    return marks.length > 0 ? { type: 'text', text, marks } : { type: 'text', text };
}
// ---------------------------------------------------------------- PM -> mdast
export function docToMdast(doc) {
    return { type: 'root', children: (doc.content ?? []).flatMap(pmToBlock) };
}
function pmToBlock(node) {
    const attrs = node.attrs ?? {};
    const children = node.content ?? [];
    switch (node.type) {
        case 'paragraph':
            return [{ type: 'paragraph', children: pmToInlines(children) }];
        case 'heading':
            return [
                {
                    type: 'heading',
                    depth: attrs['level'] ?? 1,
                    children: pmToInlines(children),
                },
            ];
        case 'codeBlock':
            return [
                {
                    type: 'code',
                    lang: attrs['language'] ?? null,
                    meta: attrs['meta'] ?? null,
                    value: children.map((c) => c.text ?? '').join(''),
                },
            ];
        case 'blockquote':
            return [{ type: 'blockquote', children: pmToBlocks(children) }];
        case 'bulletList':
        case 'orderedList': {
            const ordered = node.type === 'orderedList';
            return [
                {
                    type: 'list',
                    ordered,
                    start: ordered ? (attrs['start'] ?? 1) : null,
                    spread: attrs['spread'] ?? false,
                    children: children.map((item) => ({
                        type: 'listItem',
                        checked: item.attrs?.['checked'] ?? null,
                        spread: false,
                        children: pmToBlocks(item.content ?? []),
                    })),
                },
            ];
        }
        case 'horizontalRule':
            return [{ type: 'thematicBreak' }];
        case 'table':
            return [
                {
                    type: 'table',
                    align: attrs['align'] ?? [],
                    children: children.map((row) => ({
                        type: 'tableRow',
                        children: (row.content ?? []).map((cell) => ({
                            type: 'tableCell',
                            children: pmToInlines(cell.content ?? []),
                        })),
                    })),
                },
            ];
        case 'hint':
            return [
                container('hint', 
                // dropNull so `:::hint` is not rewritten to `:::hint{title=""}` on
                // every save — an absent title must stay absent.
                dropNull({
                    type: attrs['type'],
                    title: attrs['title'],
                }), children),
            ];
        case 'tabs':
            return [container('tabs', dropNull({ group: attrs['group'] }), children)];
        case 'tab':
            return [
                container('tab', dropNull({
                    title: attrs['title'],
                    value: attrs['value'],
                    default: attrs['default'] ? 'true' : null,
                }), children),
            ];
        case 'expandable':
            return [container('expandable', { title: attrs['title'] }, children)];
        case 'steps':
            return [container('steps', {}, children)];
        case 'step':
            return [container('step', dropNull({ title: attrs['title'] }), children)];
        case 'figure':
            return [
                container('figure', dropNull({
                    src: attrs['src'],
                    alt: attrs['alt'],
                    width: attrs['width'],
                    srcDark: attrs['srcDark'],
                }), 
                // A caption that is one empty paragraph is not a caption. The editor
                // has to insert that paragraph — ProseMirror cannot place an empty
                // non-atom block — so the choice of whether it becomes a caption
                // belongs here, at the point where the file is written.
                withoutEmptyCaption(children)),
            ];
        case 'embed':
            return [container('embed', dropNull({ url: attrs['url'] }), children)];
        case 'cards':
            return [container('cards', {}, children)];
        case 'card':
            return [
                container('card', dropNull({
                    title: attrs['title'],
                    href: attrs['href'],
                    icon: attrs['icon'],
                }), children),
            ];
        case 'file':
            return [
                container('file', dropNull({
                    src: attrs['src'],
                    name: attrs['name'],
                }), children),
            ];
        case 'button':
            return [
                {
                    type: 'leafDirective',
                    name: 'button',
                    attributes: dropNull({
                        href: attrs['href'],
                        style: attrs['style'],
                    }),
                    children: pmToInlines(children),
                },
            ];
        case 'field':
            return [
                container('field', dropNull({
                    name: attrs['name'],
                    type: attrs['type'],
                    required: attrs['required'] ? 'true' : null,
                    default: attrs['default'],
                    in: attrs['in'],
                }), children),
            ];
        case 'snippet':
            return [
                {
                    type: 'leafDirective',
                    name: 'snippet',
                    attributes: { name: attrs['name'] ?? '' },
                    children: [],
                },
            ];
        case 'codeGroup':
            return [container('codegroup', {}, children)];
        case 'genericDirective': {
            const name = attrs['name'];
            const attributes = attrs['attributes'] ?? {};
            const kind = attrs['kind'] ?? 'container';
            if (kind === 'leaf') {
                return [
                    {
                        type: 'leafDirective',
                        name,
                        attributes,
                        children: pmToInlines(children.flatMap((c) => c.content ?? [])),
                    },
                ];
            }
            return [container(name, attributes, children)];
        }
        case 'unknownBlock':
            return [structuredClone(attrs['mdast'])];
        default:
            // A block type with no mapping would be silently dropped. Refuse instead.
            throw new Error(`docToMdast: unmapped ProseMirror block node "${node.type}"`);
    }
}
function pmToBlocks(nodes) {
    return nodes.flatMap(pmToBlock);
}
/**
 * Drop a caption that holds nothing.
 *
 * The editor is obliged to insert a paragraph — ProseMirror cannot place an
 * empty non-atom block inside a paragraph — so an empty caption arrives here as
 * a real node, and whether it becomes a caption is decided at the point the file
 * is written.
 *
 * ALL empty paragraphs, not just one. Two of them used to serialise to blank
 * lines inside the directive, which the next load discarded — so the output was
 * not a fixed point, and the spacing it claimed to preserve did not survive
 * anyway. Normalising here makes it stable and honest.
 *
 * A paragraph with even a space in it is content the writer typed, and stays.
 */
function withoutEmptyCaption(children) {
    const allEmpty = children.every((node) => node.type === 'paragraph' && (node.content === undefined || node.content.length === 0));
    return allEmpty ? [] : children;
}
function container(name, attributes, children) {
    return {
        type: 'containerDirective',
        name,
        attributes: dropNull(attributes),
        children: pmToBlocks(children),
    };
}
function dropNull(attrs) {
    const out = {};
    for (const [key, value] of Object.entries(attrs)) {
        // Only absence is dropped. An explicitly authored empty string is data.
        if (value !== null && value !== undefined)
            out[key] = value;
    }
    return out;
}
function pmToInlines(nodes) {
    // Rebuild mdast's nesting from ProseMirror's flat mark lists. Adjacent nodes
    // sharing a mark must be re-wrapped in ONE parent, or `**a****b**` appears
    // where `**ab**` was written — the classic flat-to-nested round-trip bug.
    return groupByMark(nodes, 0);
}
// `underline` and `highlight` sit between `strike` and `code`: `code` stays
// innermost because inline code collapses any nesting inside it to plain text
// (see `wrapMark`'s `code` case), so nothing but the delimiter marks may
// legitimately end up "inside" it.
const MARK_ORDER = ['link', 'strong', 'em', 'strike', 'underline', 'highlight', 'code'];
function groupByMark(nodes, depth) {
    if (depth >= MARK_ORDER.length)
        return nodes.flatMap((n) => leafInline(n));
    const markType = MARK_ORDER[depth];
    const out = [];
    let run = [];
    let runMark;
    const flush = () => {
        if (run.length === 0)
            return;
        if (runMark === undefined) {
            out.push(...groupByMark(run, depth + 1));
        }
        else {
            const stripped = run.map((n) => ({
                ...n,
                marks: (n.marks ?? []).filter((m) => m.type !== markType),
            }));
            out.push(wrapMark(markType, runMark, groupByMark(stripped, depth + 1)));
        }
        run = [];
        runMark = undefined;
    };
    for (const node of nodes) {
        const mark = (node.marks ?? []).find((m) => m.type === markType);
        if (!sameMark(mark, runMark)) {
            flush();
            runMark = mark;
        }
        run.push(node);
    }
    flush();
    return out;
}
function sameMark(a, b) {
    if (a === undefined || b === undefined)
        return a === b;
    return a.type === b.type && JSON.stringify(a.attrs ?? {}) === JSON.stringify(b.attrs ?? {});
}
function wrapMark(markType, mark, children) {
    switch (markType) {
        case 'strong':
            return { type: 'strong', children };
        case 'em':
            return { type: 'emphasis', children };
        case 'strike':
            return { type: 'delete', children };
        case 'link':
            return {
                type: 'link',
                url: mark.attrs?.['href'] ?? '',
                title: mark.attrs?.['title'] ?? null,
                children,
            };
        case 'code': {
            // inlineCode holds a raw string, so any nested structure collapses to text.
            const value = children.map(phrasingToPlainText).join('');
            return { type: 'inlineCode', value };
        }
        case 'underline':
            return { type: 'textDirective', name: 'underline', attributes: {}, children };
        case 'highlight':
            return { type: 'textDirective', name: 'highlight', attributes: {}, children };
        default:
            throw new Error(`wrapMark: unknown mark "${markType}"`);
    }
}
function phrasingToPlainText(node) {
    if (node.type === 'text' || node.type === 'inlineCode')
        return node.value;
    if ('children' in node)
        return node.children.map(phrasingToPlainText).join('');
    return '';
}
function leafInline(node) {
    switch (node.type) {
        case 'text':
            return node.text === '' || node.text === undefined ? [] : [{ type: 'text', value: node.text }];
        case 'image':
            return [
                {
                    type: 'image',
                    url: node.attrs?.['src'] ?? '',
                    alt: node.attrs?.['alt'] ?? null,
                    title: node.attrs?.['title'] ?? null,
                },
            ];
        case 'hardBreak':
            return [{ type: 'break' }];
        case 'unknownInline':
            return [structuredClone(node.attrs?.['mdast'])];
        default:
            throw new Error(`docToMdast: unmapped ProseMirror inline node "${node.type}"`);
    }
}
//# sourceMappingURL=convert.js.map